import { createFileRoute } from "@tanstack/react-router";
import { routeMeta, leafCanonicalLink } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { CollectionFlagship, type FlagshipContent } from "@/components/CollectionFlagship";
import { getCollection } from "@/data/collections";

const collection = getCollection("lone-star-elite")!;

const content: FlagshipContent = {
  eyebrow: "Signature Collection · Smart Lighting · Motors · Automation",
  heroKicker:
    "Smart Outdoor Automation · San Antonio · Austin · Hill Country · Statewide Texas",
  badgeSubline:
    "Smart Lighting • Motorized Shade • App & Remote Control • Wind & Sun Sensors • Scenes",
  lifestyleIntro:
    "One tap: the awning extends, the screens drop, the string lights fade up, and the backyard turns on. When the wind kicks up, everything retracts on its own.",
  lifestyleBody:
    "The Lone Star Series is Texas Made Shade's smart lighting and automation division — the control layer that ties your outdoor living space together. Bosso Smart Lighting for landscape, pergola, and accent lighting; motorized retractable awnings, drop screens, and exterior vertical shades; app and remote control; scheduled scenes; and wind and sun sensors that protect your investment while you're away. We install and program it, and we're here when it needs service.",
  solutionsHeading: "What's under the Lone Star Series™ badge",
  solutions: [
    { title: "Smart Outdoor Lighting", desc: "Landscape, pergola, and accent lighting powered by Bosso Smart Lighting.", to: "/outdoor-lighting" },
    { title: "Automated Retractable Awnings", desc: "Motorized Solair® retractables with remote, app, and sensor control.", to: "/retractable-awnings" },
    { title: "Motorized Drop Screens", desc: "App-controlled exterior screens for sun, glare, bugs, and privacy.", to: "/exterior-vertical-shades" },
    { title: "Automated Exterior Shades", desc: "Scheduled shade that tracks the sun through the day without you touching it.", to: "/exterior-vertical-shades" },
    { title: "Wind & Sun Sensors", desc: "Auto-retract on wind, auto-extend on sun — protection when you're not home.", to: "/retractable-awnings" },
    { title: "Scenes & Scheduling", desc: "Evening, entertaining, and away scenes across lights and shade in one tap.", to: "/outdoor-lighting" },
    { title: "Outdoor Living Integration", desc: "Automation layered onto Hill Country Series™ outdoor living builds.", to: "/hill-country-series" },
    { title: "Louvered Pergola Automation", desc: "Motorized louvers, rain sensors, and integrated lighting on louvered pergolas.", to: "/louvered-pergolas" },
    { title: "Commercial Automation", desc: "Timed lighting and motorized patio screens for restaurants and retail patios.", to: "/longhorn-series" },
    { title: "Service & Motor Repair", desc: "Motor, remote, and control diagnostics and replacement through our Revival Series™ service crew.", to: "/revival-series" },
  ],
  trustBadges: [
    "Bosso Smart Lighting installer",
    "Solair® Authorized Dealer",
    "Programmed & supported locally",
    "Financing available — Acorn Finance",
  ],
  faqs: [
    {
      q: "What does the Lone Star Series™ badge cover?",
      a: "Everything that makes your outdoor space run itself: smart lighting, motorized awnings and screens, app and remote control, scenes and schedules, and wind and sun sensors.",
    },
    {
      q: "Do I control it from my phone?",
      a: "Yes — app control plus handheld remotes and wall controls. We set up the scenes with you at handoff so it works the way you actually use the patio.",
    },
    {
      q: "Will an awning retract by itself in high wind?",
      a: "With a wind sensor, yes. It's one of the smartest upgrades on a motorized retractable in Texas, because gusts do more damage than sun.",
    },
    {
      q: "Who makes the smart lighting?",
      a: "We install Bosso Smart Lighting for landscape, pergola, and accent lighting. Bosso is the manufacturer we represent; the design, install, and programming are ours.",
    },
    {
      q: "Can you automate shade I already own?",
      a: "Often, yes. Send photos of the system, motor, and controls and we'll tell you whether it can be motorized or brought onto a smart control.",
    },
    {
      q: "Is financing available on automation packages?",
      a: "Yes — smart lighting, motorized shade, and outdoor living packages qualify for Acorn Finance. Financing available on approved credit.",
    },
    {
      q: "Where do you install?",
      a: "San Antonio, Austin, New Braunfels, Seguin, Boerne, Bulverde, Fredericksburg, Kerrville, Lake Travis, and statewide Texas for larger projects.",
    },
  ],
};

export const Route = createFileRoute("/lone-star-elite")({
  head: (ctx) => ({
    meta: routeMeta(ctx, {
      title: "Lone Star Series™ — Smart Outdoor Lighting & Shade Automation | Texas Made Shade",
      description:
        "Lone Star Series™ — smart outdoor lighting, motorized awnings and screens, app control, scenes and wind sensors. San Antonio, Austin and statewide Texas.",
      keywords:
        "smart outdoor lighting san antonio, smart outdoor lighting austin, bosso smart lighting texas, motorized awning automation, app controlled patio screens, motorized drop screens austin, wind sensor retractable awning, sun sensor awning, landscape lighting automation texas, pergola lighting san antonio, smart shade automation texas, outdoor lighting control system",
    }),
    links: leafCanonicalLink(ctx, collection.path),
    scripts: serviceHeadScripts({
      path: collection.path,
      serviceName: "Lone Star Series Smart Lighting & Shade Automation",
      serviceType: "Smart outdoor lighting, motorized shade & automation",
      description: collection.description,
      faqs: content.faqs,
      breadcrumbName: "Lone Star Series — Smart Lighting & Automation",
    }),
  }),
  component: () => <CollectionFlagship collection={collection} content={content} />,
});
